import React from 'react';

import type { IntlShape } from 'react-intl-next';

import { aiDefinitionsMessages } from '@atlaskit/editor-common/ai-messages';
import type {
	Command,
	ExtractInjectionAPI,
	FloatingToolbarButton,
} from '@atlaskit/editor-common/types';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
import { AtlassianIcon, RovoIcon } from '@atlaskit/logo';

import type { AIDefinitionsPlugin } from '../../aiDefinitionsPluginType';
import type { AIDefinitionsPluginConfig } from '../../types';
import {
	getReadingAidsSelectionProps,
	selectionExceedsMaxWordCount,
} from '../../ui/utils/selection';
import { openReadingAidsPopup } from '../ai-definitions/commands';
import { getPluginState } from '../ai-definitions/plugin-factory';

export const getAIDefineButton = ({
	intl,
	state,
	config,
	api,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin>;
	config: AIDefinitionsPluginConfig;
	intl: IntlShape;
	state: EditorState;
}) => {
	const pluginState = getPluginState(state);
	const readingAidsPopupProps = getReadingAidsSelectionProps(state);
	const disabled = selectionExceedsMaxWordCount(state, config) || !readingAidsPopupProps;

	return {
		title: intl.formatMessage(aiDefinitionsMessages.aiDefineToolbarButtonTitle),
		type: 'button',
		showTitle: true,
		testId: 'ak-editor-ai-selection-toolbar-define-button',
		icon: () =>
			pluginState.enablementSource === 'rovo' ? (
				<RovoIcon size="small" />
			) : (
				<AtlassianIntelligenceIcon label="" />
			),
		iconFallback: () => <AtlassianIcon size="small" />,
		tooltipContent: disabled
			? intl.formatMessage(aiDefinitionsMessages.aiDefineToolbarButtonDisabledTooltip)
			: undefined,
		disabled: disabled,
		onClick: readingAidsPopupProps
			? openReadingAidsPopup({
					api,
					source: 'define-button-click',
					readingAidsPopupProps,
				})
			: undefined,
		supportsViewMode: true,
	} as FloatingToolbarButton<Command>;
};

export const getAIDefineOverflowOption = ({
	intl,
	state,
	config,
	api,
}: {
	api: ExtractInjectionAPI<AIDefinitionsPlugin>;
	config: AIDefinitionsPluginConfig;
	intl: IntlShape;
	state: EditorState;
}): {
	disabled: boolean;
	icon: React.JSX.Element;
	iconFallback: () => React.JSX.Element;
	onClick: Command | undefined;
	rank: number;
	title: string;
	tooltipContent: string | undefined;
} => {
	const pluginState = getPluginState(state);
	const readingAidsPopupProps = getReadingAidsSelectionProps(state);
	const disabled = selectionExceedsMaxWordCount(state, config) || !readingAidsPopupProps;

	return {
		title: intl.formatMessage(aiDefinitionsMessages.aiDefineToolbarButtonTitle),
		icon:
			pluginState.enablementSource === 'rovo' ? (
				<RovoIcon size="small" />
			) : (
				<AtlassianIntelligenceIcon label="" />
			),
		iconFallback: (): React.JSX.Element => <AtlassianIcon size="small" />,
		tooltipContent: disabled
			? intl.formatMessage(aiDefinitionsMessages.aiDefineToolbarButtonDisabledTooltip)
			: undefined,
		disabled: disabled,
		onClick: readingAidsPopupProps
			? openReadingAidsPopup({
					api,
					source: 'define-button-click',
					readingAidsPopupProps,
				})
			: undefined,
		rank: 20,
	};
};
